/**
 * Theme adapter for ADC AU site — injects real next/font families.
 */
import type { Theme } from '@emotion/react';
import { buildTheme } from 'common-ui';
import type { CommonThemeVars, ThemeCreateFunction } from 'tsconfig/types.d';
import { notoSerifFont, oswaldFont, robotoFont } from 'common-ui/src/theme/theme/sharedNextFont';
import {
  buildThemeVarsAPACAdc as buildThemeVarsAPACAdcFoundation,
  buildThemeMixinsAPACAdc as buildThemeMixinsAPACAdcFoundation,
  buildThemeWidgetStylesAPACAdc as buildThemeWidgetStylesAPACAdcFoundation,
  buildThemeWidgetStylesAPACAdcFromBase,
  buildThemeWidgetStylesAPACLWFromBase,
  buildCoreWidgets,
  buildProductCardWidgets,
  buildProductWidgets,
  buildSearchWidgets,
  buildMiniCartWidgets,
  buildCartWidgets,
  buildAccountWidgets,
  buildLandingpageWidgets,
} from './themeFoundation';

export const buildThemeVarsAPACAdc = (
  vars: Pick<Theme, CommonThemeVars>,
): ReturnType<typeof buildThemeVarsAPACAdcFoundation> =>
  buildThemeVarsAPACAdcFoundation(vars, {
    oswald: oswaldFont.style.fontFamily,
    roboto: robotoFont.style.fontFamily,
    noto: notoSerifFont.style.fontFamily,
  });

export const buildThemeMixinsAPACAdc = (
  vars: Omit<Theme, 'mixins' | 'widgets'>,
): ReturnType<typeof buildThemeMixinsAPACAdcFoundation> => buildThemeMixinsAPACAdcFoundation(vars);

export const buildThemeWidgetStylesAPACAdc = (
  vars: Omit<Theme, 'widgets'>,
): ReturnType<typeof buildThemeWidgetStylesAPACAdcFoundation> => buildThemeWidgetStylesAPACAdcFoundation(vars);

// CMS pages: core + product cards + minicart (header)
export const createCmsPageTheme: ThemeCreateFunction = () =>
  buildTheme(buildThemeVarsAPACAdc, buildThemeMixinsAPACAdc, (vars: Omit<Theme, 'widgets'>) =>
    buildThemeWidgetStylesAPACAdcFromBase(
      vars,
      buildThemeWidgetStylesAPACLWFromBase(vars, {
        ...buildCoreWidgets(vars),
        ...buildProductCardWidgets(vars),
        ...buildMiniCartWidgets(vars),
      }),
    ),
  );

// PDP: core + full product + minicart (header)
export const createProductTheme: ThemeCreateFunction = () =>
  buildTheme(buildThemeVarsAPACAdc, buildThemeMixinsAPACAdc, (vars: Omit<Theme, 'widgets'>) =>
    buildThemeWidgetStylesAPACAdcFromBase(
      vars,
      buildThemeWidgetStylesAPACLWFromBase(vars, {
        ...buildCoreWidgets(vars),
        ...buildProductWidgets(vars),
        ...buildMiniCartWidgets(vars),
      }),
    ),
  );

// Search/PLP: core + search + product cards + minicart
export const createSearchTheme: ThemeCreateFunction = () =>
  buildTheme(buildThemeVarsAPACAdc, buildThemeMixinsAPACAdc, (vars: Omit<Theme, 'widgets'>) =>
    buildThemeWidgetStylesAPACAdcFromBase(
      vars,
      buildThemeWidgetStylesAPACLWFromBase(vars, {
        ...buildCoreWidgets(vars),
        ...buildSearchWidgets(vars),
        ...buildProductWidgets(vars),
        ...buildMiniCartWidgets(vars),
      }),
    ),
  );

// Cart: core + cart + minicart
export const createCartTheme: ThemeCreateFunction = () =>
  buildTheme(buildThemeVarsAPACAdc, buildThemeMixinsAPACAdc, (vars: Omit<Theme, 'widgets'>) =>
    buildThemeWidgetStylesAPACAdcFromBase(
      vars,
      buildThemeWidgetStylesAPACLWFromBase(vars, {
        ...buildCoreWidgets(vars),
        ...buildCartWidgets(vars),
      }),
    ),
  );

// Account: core + account + minicart (header)
export const createAccountTheme: ThemeCreateFunction = () =>
  buildTheme(buildThemeVarsAPACAdc, buildThemeMixinsAPACAdc, (vars: Omit<Theme, 'widgets'>) =>
    buildThemeWidgetStylesAPACAdcFromBase(
      vars,
      buildThemeWidgetStylesAPACLWFromBase(vars, {
        ...buildCoreWidgets(vars),
        ...buildAccountWidgets(vars),
        ...buildMiniCartWidgets(vars),
      }),
    ),
  );

// Landing/offer pages: core + landing page + minicart
export const createLandingPageTheme: ThemeCreateFunction = () =>
  buildTheme(buildThemeVarsAPACAdc, buildThemeMixinsAPACAdc, (vars: Omit<Theme, 'widgets'>) =>
    buildThemeWidgetStylesAPACAdcFromBase(
      vars,
      buildThemeWidgetStylesAPACLWFromBase(vars, {
        ...buildCoreWidgets(vars),
        ...buildLandingpageWidgets(vars),
        ...buildMiniCartWidgets(vars),
      }),
    ),
  );

const createTheme: ThemeCreateFunction = () =>
  buildTheme(buildThemeVarsAPACAdc, buildThemeMixinsAPACAdc, buildThemeWidgetStylesAPACAdc);
export default createTheme;
